import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../components/ui/use-toast';
import { adminContent } from '../../services/api';
import { MediaUploadJob } from 'types';

type UploadSource = 'google_drive' | 'local_upload';

type SelectedFileEntry = {
  id: string;
  file: File;
};

const LOCAL_UPLOAD_LIMIT_LABEL = 'Up to 100 files, 512 MB per file, 5 GB total per batch.';
const NO_JOB_VALUE = '__none__';

const buildSelectedFileId = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

const UploadMedia: React.FC = () => {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<UploadSource>('google_drive');
  const [driveUrl, setDriveUrl] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileEntry[]>([]);
  const [recentJobs, setRecentJobs] = useState<MediaUploadJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [job, setJob] = useState<MediaUploadJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingRecentJobs, setLoadingRecentJobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusNotifiedRef = useRef<string | null>(null);

  const isJobActive = job ? ['pending', 'running'].includes(job.status) : false;

  const mergeRecentJob = useCallback((nextJob: MediaUploadJob) => {
    setRecentJobs((current) => {
      const withoutExisting = current.filter((candidate) => candidate.id !== nextJob.id);
      return [nextJob, ...withoutExisting].slice(0, 20);
    });
  }, []);

  const loadRecentJobs = useCallback(async () => {
    setLoadingRecentJobs(true);
    try {
      const response = await adminContent.mediaUpload.list();
      setRecentJobs((response.data as MediaUploadJob[]) ?? []);
    } catch (err) {
      addToast({
        title: 'Unable to load recent uploads',
        description: 'Past media upload jobs could not be loaded.',
        variant: 'destructive',
      });
    } finally {
      setLoadingRecentJobs(false);
    }
  }, [addToast]);

  const fetchJobStatus = useCallback(
    async (jobId: string) => {
      const response = await adminContent.mediaUpload.retrieve(jobId);
      const nextJob = response.data as MediaUploadJob;
      setJob(nextJob);
      setSelectedJobId(nextJob.id);
      mergeRecentJob(nextJob);
      return nextJob;
    },
    [mergeRecentJob]
  );

  useEffect(() => {
    loadRecentJobs().catch(() => null);
  }, [loadRecentJobs]);

  const handleStartUpload = useCallback(async () => {
    const trimmedUrl = driveUrl.trim();

    if (source === 'google_drive' && !trimmedUrl) {
      setError('Please provide a Google Drive folder URL.');
      return;
    }

    if (source === 'local_upload' && selectedFiles.length === 0) {
      setError('Please select one or more files from your computer.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response =
        source === 'google_drive'
          ? await adminContent.mediaUpload.startDrive({ drive_url: trimmedUrl })
          : await adminContent.mediaUpload.startLocal(selectedFiles.map((entry) => entry.file));
      const jobData = response.data as MediaUploadJob;
      setJob(jobData);
      setSelectedJobId(jobData.id);
      statusNotifiedRef.current = jobData.status;
      mergeRecentJob(jobData);
      if (source === 'local_upload') {
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      addToast({
        title: source === 'google_drive' ? 'Drive import queued' : 'Local upload queued',
        description: String(jobData.summary?.message ?? 'Processing will continue in the background.'),
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ??
        err?.response?.data?.error ??
        err?.message ??
        'Media upload failed.';
      setError(message);
      addToast({ title: 'Upload failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }, [addToast, driveUrl, mergeRecentJob, selectedFiles, source]);

  const handleRefresh = useCallback(async () => {
    if (!job) {
      return;
    }

    setRefreshing(true);
    try {
      await fetchJobStatus(job.id);
    } catch (err) {
      addToast({
        title: 'Refresh failed',
        description: 'Unable to refresh job status.',
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
    }
  }, [addToast, fetchJobStatus, job]);

  const handleSelectPastJob = useCallback(
    async (jobId: string) => {
      if (jobId === NO_JOB_VALUE) {
        setSelectedJobId(null);
        setJob(null);
        return;
      }

      const cachedJob = recentJobs.find((candidate) => candidate.id === jobId) ?? null;
      if (cachedJob) {
        setJob(cachedJob);
      }
      setSelectedJobId(jobId);
      setRefreshing(true);
      try {
        await fetchJobStatus(jobId);
      } catch (err) {
        addToast({
          title: 'Unable to load upload job',
          description: 'The selected upload job could not be loaded.',
          variant: 'destructive',
        });
      } finally {
        setRefreshing(false);
      }
    },
    [addToast, fetchJobStatus, recentJobs]
  );

  useEffect(() => {
    if (!job || !isJobActive) {
      return;
    }
    const interval = setInterval(() => {
      fetchJobStatus(job.id).catch(() => null);
    }, 4000);
    return () => clearInterval(interval);
  }, [fetchJobStatus, isJobActive, job]);

  useEffect(() => {
    if (!job || !['completed', 'failed'].includes(job.status)) {
      return;
    }
    if (statusNotifiedRef.current === job.status) {
      return;
    }
    statusNotifiedRef.current = job.status;

    const summary = (job.details?.summary ?? {}) as Record<string, unknown>;
    const uploadedFiles =
      typeof summary['uploaded_files'] === 'number' ? summary['uploaded_files'] : job.uploaded_files ?? 0;

    if (job.status === 'completed') {
      addToast({
        title: 'Media import completed',
        description: `${uploadedFiles} file(s) uploaded successfully.`,
      });
    } else {
      addToast({
        title: 'Media import failed',
        description: job.errors?.[0] ?? 'Review the job details for more information.',
        variant: 'destructive',
      });
    }
  }, [addToast, job]);

  const appendSelectedFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setSelectedFiles((current) => {
      const existingIds = new Set(current.map((entry) => entry.id));
      const nextEntries = Array.from(files).reduce<SelectedFileEntry[]>((entries, file) => {
        const id = buildSelectedFileId(file);
        if (existingIds.has(id)) {
          return entries;
        }
        existingIds.add(id);
        entries.push({ id, file });
        return entries;
      }, []);
      return nextEntries.length ? [...current, ...nextEntries] : current;
    });
  }, []);

  const removeSelectedFile = useCallback((fileId: string) => {
    setSelectedFiles((current) => current.filter((entry) => entry.id !== fileId));
  }, []);

  const handleLocalFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      appendSelectedFiles(event.target.files);
      event.target.value = '';
    },
    [appendSelectedFiles]
  );

  const toNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatJobOptionLabel = useCallback((jobOption: MediaUploadJob) => {
    const sourceLabel = jobOption.source_type === 'local_upload' ? 'Local' : 'Drive';
    const createdLabel = new Date(jobOption.created_at).toLocaleString();
    return `${sourceLabel} • ${createdLabel}`;
  }, []);

  const jobDetails = (job?.details ?? {}) as Record<string, unknown>;
  const sourceDetails =
    typeof jobDetails['source'] === 'object' && jobDetails['source'] !== null
      ? (jobDetails['source'] as Record<string, unknown>)
      : {};
  const summary =
    typeof jobDetails['summary'] === 'object' && jobDetails['summary'] !== null
      ? (jobDetails['summary'] as Record<string, unknown>)
      : {};
  const totalFiles = job?.total_files ?? toNumber(summary['total_files']);
  const processed = toNumber(summary['processed_files']);
  const uploaded = job?.uploaded_files ?? toNumber(summary['uploaded_files']);
  const duplicates = toNumber(summary['duplicate_files']);
  const unsupported = toNumber(summary['unsupported_files']);
  const summaryErrors = Array.isArray(summary['errors']) ? (summary['errors'] as string[]) : [];
  const errorCount = job?.error_count ?? toNumber(summary['error_count'] ?? summaryErrors.length);
  const progressPercent = totalFiles > 0 ? Math.min(100, Math.round((processed / totalFiles) * 100)) : 0;
  const sourceLabel =
    job?.source_type === 'local_upload' ? 'Local Upload' : job?.source_type === 'google_drive' ? 'Google Drive' : null;
  const totalFilesCaption =
    job?.source_type === 'local_upload' ? 'Files selected from your computer' : 'Files discovered in the Drive folder';
  const metrics: Array<{ label: string; value: number; caption?: string }> = [
    {
      label: 'Total files',
      value: totalFiles,
      caption: totalFilesCaption,
    },
    {
      label: 'Files processed',
      value: processed,
      caption: 'Files examined by the worker',
    },
    {
      label: 'Uploaded',
      value: uploaded,
      caption: 'New assets added to storage',
    },
    {
      label: 'Already in library',
      value: duplicates,
      caption: 'Assets already present in storage',
    },
    {
      label: 'Unsupported',
      value: unsupported,
      caption: 'Skipped because the file type is not supported',
    },
    {
      label: 'Errors',
      value: errorCount,
      caption: errorCount ? 'See details below' : 'No upload errors',
    },
  ];

  const selectedJobValue = selectedJobId ?? NO_JOB_VALUE;
  const selectedFilesCountLabel = useMemo(
    () => `${selectedFiles.length.toLocaleString()} file(s) selected`,
    [selectedFiles.length]
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <Card maxWidth="none">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle title="Upload Media" />
          </div>
          <div className="w-full sm:w-72">
            <Label htmlFor="recent-media-upload-jobs">Recent uploads</Label>
            <Select value={selectedJobValue} onValueChange={handleSelectPastJob}>
              <SelectTrigger id="recent-media-upload-jobs" className="mt-2 w-full" disabled={loadingRecentJobs}>
                <SelectValue placeholder={loadingRecentJobs ? 'Loading recent uploads...' : 'Select past upload'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_JOB_VALUE}>No job selected</SelectItem>
                {recentJobs.map((jobOption) => (
                  <SelectItem key={jobOption.id} value={jobOption.id}>
                    {formatJobOptionLabel(jobOption)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="w-full space-y-2">
            <Label>Source</Label>
            <div className="flex flex-row items-center gap-2">
              <Button
                type="button"
                variant={source === 'google_drive' ? 'default' : 'secondary'}
                className="w-auto px-4 py-2"
                onClick={() => {
                  setSource('google_drive');
                  setError(null);
                }}
              >
                Google Drive
              </Button>
              <Button
                type="button"
                variant={source === 'local_upload' ? 'default' : 'secondary'}
                className="w-auto px-4 py-2"
                onClick={() => {
                  setSource('local_upload');
                  setError(null);
                }}
              >
                Local Upload
              </Button>
            </div>
          </div>

          {source === 'google_drive' ? (
            <div className="w-full space-y-2">
              <Label htmlFor="drive-url">Google Drive Folder URL</Label>
              <Input
                id="drive-url"
                placeholder="https://drive.google.com/drive/folders/..."
                value={driveUrl}
                onChange={(event) => setDriveUrl(event.target.value)}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">Files with unsupported file types will be skipped.</p>
            </div>
          ) : (
            <div className="w-full space-y-3">
              <div className="w-full space-y-2">
                <Label htmlFor="local-media-files">Files from your computer</Label>
                <Input
                  id="local-media-files"
                  type="file"
                  multiple
                  onChange={handleLocalFileChange}
                  disabled={submitting}
                  data-testid="local-media-files"
                  className="bg-black/10"
                  ref={fileInputRef}
                />
                <p className="text-xs text-muted-foreground">{LOCAL_UPLOAD_LIMIT_LABEL}</p>
              </div>

              {selectedFiles.length > 0 ? (
                <div className="w-full rounded-lg border border-border/60 bg-muted/20 p-4">
                  <div className="text-sm font-medium text-foreground">{selectedFilesCountLabel}</div>
                  <div className="mt-3 space-y-2">
                    {selectedFiles.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-background/60 px-3 py-2"
                      >
                        <span className="min-w-0 truncate text-sm text-foreground">{entry.file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-auto px-2 py-1"
                          onClick={() => removeSelectedFile(entry.id)}
                          aria-label={`Remove ${entry.file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No files selected yet.</p>
              )}
            </div>
          )}

          {error ? <p className="w-full text-sm text-destructive">{error}</p> : null}

          <div className="flex w-full">
            <Button type="button" onClick={handleStartUpload} disabled={submitting} className="w-full" size="auto">
              {submitting ? <Spinner size="sm" /> : source === 'google_drive' ? 'Import from Drive' : 'Upload Files'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {job ? (
        <Card maxWidth="none">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <CardTitle title={`Status: ${job.status.toUpperCase()}`} />
            </div>
            <Button type="button" variant="secondary" onClick={handleRefresh} disabled={refreshing} className="w-auto">
              {refreshing ? 'Refreshing...' : 'Refresh status'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <span className="font-medium text-foreground">Job ID:</span> {job.id}
              </div>
              {sourceLabel ? (
                <div>
                  <span className="font-medium text-foreground">Source:</span> {sourceLabel}
                </div>
              ) : null}
              {job.drive_url ? (
                <div className="md:col-span-2">
                  <span className="font-medium text-foreground">Drive URL:</span> {job.drive_url}
                </div>
              ) : null}
              {job.source_type === 'local_upload' ? (
                <div>
                  <span className="font-medium text-foreground">Batch size:</span>{' '}
                  {toNumber(sourceDetails['file_count']).toLocaleString()} file(s)
                </div>
              ) : null}
              <div>
                <span className="font-medium text-foreground">Created:</span> {new Date(job.created_at).toLocaleString()}
              </div>
              {job.started_at ? (
                <div>
                  <span className="font-medium text-foreground">Started:</span> {new Date(job.started_at).toLocaleString()}
                </div>
              ) : null}
              {job.finished_at ? (
                <div>
                  <span className="font-medium text-foreground">Finished:</span> {new Date(job.finished_at).toLocaleString()}
                </div>
              ) : null}
            </div>

            {isJobActive ? (
              <div className="w-full space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    Processing {processed.toLocaleString()} / {totalFiles.toLocaleString()} files
                  </span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {metrics.map((metric) => (
                <div key={metric.label} className="rounded-lg border border-border/60 bg-muted/20 p-4">
                  <div className="text-xs uppercase text-muted-foreground">{metric.label}</div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">{metric.value.toLocaleString()}</div>
                  {metric.caption ? <p className="mt-2 text-xs text-muted-foreground">{metric.caption}</p> : null}
                </div>
              ))}
            </div>

            {job.errors?.length ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
                <h3 className="mb-2 font-semibold uppercase">Errors</h3>
                <ul className="list-disc space-y-1 pl-5">
                  {job.errors.map((message, index) => (
                    <li key={`${message}-${index}`}>{message}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default UploadMedia;
